// src/pages/CreditApplications.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  CreditCard,
  Plus,
  Search,
  Filter,
  Copy,
  ExternalLink,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  FileText,
  ShieldCheck,
  Send,
  MoreVertical,
  Calendar,
  Building2,
  User,
  Settings,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { CreditApplicationListItem } from '../types/creditApplications';

export const CreditApplications: React.FC = () => {
  const { user, can } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<CreditApplicationListItem[]>([]);
  const [metrics, setMetrics] = useState<any>({
    total: 0,
    draft: 0,
    link_sent: 0,
    in_progress: 0,
    submitted: 0,
    under_review: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [salespersonFilter, setSalespersonFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Create Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newSalesperson, setNewSalesperson] = useState(user?.fullName || '');
  const [newRegion, setNewRegion] = useState('UAE / Regional');
  const [newExpiryDays, setNewExpiryDays] = useState('14');
  const [newNotes, setNewNotes] = useState('');

  // Link Created Modal
  const [createdLinkInfo, setCreatedLinkInfo] = useState<{
    reference_no: string;
    customer_url: string;
    expires_at: string;
  } | null>(null);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '15',
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      if (salespersonFilter) params.append('salesperson', salespersonFilter);

      const res = await fetch(`/api/credit-applications?${params.toString()}`);
      const data = await res.json<any>();
      if (res.ok && data.success) {
        setItems(data.data.items);
        setMetrics(data.data.metrics);
        setTotalPages(data.data.pagination.totalPages);
        setTotalCount(data.data.pagination.total);
      }
    } catch (err: any) {
      showToast('Failed to fetch credit applications.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [page, statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchApplications();
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompany.trim() || !newEmail.trim() || !newPhone.trim()) {
      showToast('Company name, email, and phone are required.', 'error');
      return;
    }

    setCreating(true);
    try {
      const res = await fetch('/api/credit-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: newCompany.trim(),
          primary_email: newEmail.trim(),
          primary_phone: newPhone.trim(),
          salesperson_name: newSalesperson,
          sales_region: newRegion,
          expiry_days: Number(newExpiryDays),
          internal_notes: newNotes,
        }),
      });

      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to create application');
      }

      showToast('Credit application created.', 'success');
      setShowCreateModal(false);
      setCreatedLinkInfo({
        reference_no: data.data.reference_no,
        customer_url: `${window.location.origin}${data.data.customer_url}`,
        expires_at: data.data.expires_at,
      });

      // Reset form
      setNewCompany('');
      setNewEmail('');
      setNewPhone('');
      setNewNotes('');

      fetchApplications();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Link copied to clipboard!', 'success');
  };

  const getStatusBadge = (status: string) => {
    return <Badge status={status} />;
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[#0f5a2e]" />
            Customer Credit Applications
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Intake workflow, digital verification evidence, and commercial credit facility assessment (GIND/CIF/01/2026).
          </p>
        </div>

        <div className="flex items-center gap-2">
          {can('credit_applications.create') && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white flex items-center gap-2 text-xs"
            >
              <Plus className="w-4 h-4" />
              <span>+ New Credit Application</span>
            </Button>
          )}
        </div>
      </div>

      {/* Metrics Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <span className="text-[11px] font-medium text-slate-500 uppercase">Total</span>
          <div className="text-lg font-bold text-slate-900 mt-0.5">{metrics.total}</div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <span className="text-[11px] font-medium text-blue-600 uppercase">Submitted</span>
          <div className="text-lg font-bold text-blue-700 mt-0.5">{metrics.submitted}</div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <span className="text-[11px] font-medium text-amber-600 uppercase">In Progress</span>
          <div className="text-lg font-bold text-amber-700 mt-0.5">
            {metrics.in_progress + metrics.link_sent}
          </div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <span className="text-[11px] font-medium text-purple-600 uppercase">Under Review</span>
          <div className="text-lg font-bold text-purple-700 mt-0.5">{metrics.under_review}</div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <span className="text-[11px] font-medium text-emerald-600 uppercase">Approved</span>
          <div className="text-lg font-bold text-emerald-700 mt-0.5">{metrics.approved}</div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <span className="text-[11px] font-medium text-red-600 uppercase">Rejected</span>
          <div className="text-lg font-bold text-red-700 mt-0.5">{metrics.rejected}</div>
        </div>
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <span className="text-[11px] font-medium text-slate-500 uppercase">Drafts</span>
          <div className="text-lg font-bold text-slate-600 mt-0.5">{metrics.draft}</div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by company name, reference number, or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 text-xs"
            />
          </div>

          <div className="w-full sm:w-48">
            <Select
              value={statusFilter}
              onChange={e => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'draft', label: 'Draft' },
                { value: 'link_sent', label: 'Link Sent' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'submitted', label: 'Submitted' },
                { value: 'under_review', label: 'Under Review' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'expired', label: 'Expired' },
              ]}
            />
          </div>

          <Button type="submit" variant="outline" size="sm" className="text-xs">
            Search
          </Button>
        </form>
      </div>

      {/* Applications Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                <th className="py-3 px-4">Ref Number</th>
                <th className="py-3 px-4">Customer Company</th>
                <th className="py-3 px-4">Requested Facility</th>
                <th className="py-3 px-4">Salesperson</th>
                <th className="py-3 px-4">Created Date</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">
                    <div className="w-6 h-6 border-2 border-[#0f5a2e] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    Loading applications...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">
                    No credit applications found matching criteria.
                  </td>
                </tr>
              ) : (
                items.map(app => (
                  <tr key={app.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-3 px-4 font-mono font-bold text-slate-800">
                      <Link to={`/credit-applications/${app.id}`} className="hover:text-[#0f5a2e] hover:underline">
                        {app.reference_no}
                      </Link>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-900">{app.company_name}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{app.primary_email}</div>
                    </td>
                    <td className="py-3 px-4">
                      {app.requested_credit_limit ? (
                        <div>
                          <span className="font-bold text-[#0f5a2e]">
                            {app.currency} {Number(app.requested_credit_limit).toLocaleString('en-IN')}
                          </span>
                          <span className="text-[11px] text-slate-400 block">
                            {app.requested_credit_terms || '30 Days'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">Not entered yet</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-slate-800 font-medium">{app.salesperson_name}</div>
                      <div className="text-[11px] text-slate-400">{app.sales_region}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-600 font-mono">
                      {app.created_at.split(' ')[0]}
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Link to={`/credit-applications/${app.id}`}>
                          <Button variant="outline" size="sm" className="text-xs py-1 px-2.5">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="p-3 border-t border-slate-200">
            <Pagination
              page={page}
              pageSize={15}
              total={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Modal: Create Application */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Credit Application"
      >
        <form onSubmit={handleCreateSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Customer / Company Name *</label>
            <Input
              required
              placeholder="e.g. Al Futtaim Contracting LLC"
              value={newCompany}
              onChange={e => setNewCompany(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Primary Contact Email *</label>
              <Input
                type="email"
                required
                placeholder="purchasing@company.com"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Primary Contact Phone *</label>
              <Input
                required
                placeholder="+971 50 123 4567"
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Salesperson</label>
              <Input
                value={newSalesperson}
                onChange={e => setNewSalesperson(e.target.value)}
              />
            </div>
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Sales Region / Territory</label>
              <Input
                value={newRegion}
                onChange={e => setNewRegion(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Link Expiry (Days)</label>
              <Select
                value={newExpiryDays}
                onChange={e => setNewExpiryDays(e.target.value)}
                options={[
                  { value: '7', label: '7 Days' },
                  { value: '14', label: '14 Days (Default)' },
                  { value: '30', label: '30 Days' },
                  { value: '60', label: '60 Days' },
                ]}
              />
            </div>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">Internal Notes</label>
            <textarea
              rows={2}
              className="w-full text-xs p-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e]"
              placeholder="Any background notes for the sales or credit team..."
              value={newNotes}
              onChange={e => setNewNotes(e.target.value)}
            />
          </div>

          <div className="pt-3 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating}
              className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white"
            >
              {creating ? 'Creating...' : 'Create & Generate Link'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: Link Generated Information */}
      {createdLinkInfo && (
        <Modal
          isOpen={true}
          onClose={() => setCreatedLinkInfo(null)}
          title="Customer Link Generated"
        >
          <div className="space-y-4 text-xs">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 flex items-start gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold block">Secure Credit Application Created</span>
                <span className="text-xs">
                  Reference: <strong>{createdLinkInfo.reference_no}</strong> (Expires {createdLinkInfo.expires_at.split('T')[0]})
                </span>
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-1">
                Share this secure link with the customer via WhatsApp or Email:
              </label>
              <div className="flex gap-2">
                <Input value={createdLinkInfo.customer_url} readOnly className="font-mono text-xs" />
                <Button
                  type="button"
                  onClick={() => copyToClipboard(createdLinkInfo.customer_url)}
                  className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white flex-shrink-0 flex items-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" />
                  <span>Copy</span>
                </Button>
              </div>
            </div>

            <div className="pt-3 flex justify-end">
              <Button type="button" onClick={() => setCreatedLinkInfo(null)}>
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
